import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { AppState } from '../../stores/redux-store';
import {
  Credentials,
  CredentialsEntity,
  CredentialsStatus,
  CredentialsType,
} from '../../models/credentials';
import { pickTextFromWithinDoubleQuotes } from '../../utilities/utils';
import {
  setApplicationError as setApplicationErrorConnect,
  setCredentialsError as setCredentialsErrorConnect,
} from '../../stores/app-state-store';
import { setCredentialsErrorMessage as setCredentialsErrorMessageConnect } from '../../stores/credentials-store';
import { isSwedbankOrSavingsbank } from '../../payments/legacy/components/PaymentDirector/molecules/SwedbankSavingsbankErrorOverlay/SwedbankSavingsbankErrorOverlay';
import { UseProvider, useProvider as useProviderImport } from '../hooks/useProvider';

type CredentialsErrorBoundaryProps = PropsFromRedux & {
  children: React.ReactNode;
  useProvider?: UseProvider;
};

export const CredentialsErrorBoundaryPresenter: React.FC<CredentialsErrorBoundaryProps> = ({
  children,
  credentials,
  setCredentialsError,
  setApplicationError,
  setCredentialsErrorMessage,
  useProvider = useProviderImport,
}) => {
  const [credentialsType, setCredentialsType] = useState<CredentialsType | undefined>(undefined);

  const isSwedbankError =
    credentials &&
    isSwedbankOrSavingsbank(credentials.providerName) &&
    Credentials.isSwedbankSavingsbankError(credentials);

  const providerName = credentials?.providerName;
  const credentialsHasError = Credentials.hasError(credentials);

  const provider = useProvider({ shouldFetch: credentialsHasError, providerName });

  useEffect(() => {
    if (provider) {
      setCredentialsType(provider.credentialsType);
    }
  }, [provider]);

  useEffect(() => {
    if (credentialsHasError) {
      const errorMessage = pickTextFromWithinDoubleQuotes(credentials?.statusPayload);
      setCredentialsErrorMessage(errorMessage);
    }
  }, [credentials?.statusPayload, credentialsHasError, setCredentialsErrorMessage]);

  const isFatalCredentialsType = credentialsType
    ? [CredentialsType.MOBILE_BANKID, CredentialsType.THIRD_PARTY_APP].includes(credentialsType)
    : false;

  const isFatalError = credentialsHasError && isFatalCredentialsType && !isSwedbankError;

  const throwCredentialsError = useCallback(
    (errorCredentials: CredentialsEntity) => {
      const {
        detailedError: credentialsDetailedError,
        id: credentialsId,
        status,
      } = errorCredentials;

      if (status === CredentialsStatus.TEMPORARY_ERROR) {
        setApplicationError({
          errorStatus: ErrorStatus.TEMPORARY_ERROR,
          providerName,
          credentialsDetailedError,
          errorReason: ErrorReason.INVALID_STATE_CREDENTIALS,
        });
      } else {
        setCredentialsError({
          errorStatus: ErrorStatus.AUTHENTICATION_ERROR,
          credentialsId,
          providerName,
          credentialsDetailedError,
          errorReason: ErrorReason.INVALID_STATE_AUTHENTICATION,
        });
      }
    },
    [setApplicationError, setCredentialsError, providerName]
  );

  useEffect(() => {
    if (isFatalError && credentials) {
      throwCredentialsError(credentials);
    }
  }, [credentials, isFatalError, throwCredentialsError]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
  };
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorConnect,
  setCredentialsError: setCredentialsErrorConnect,
  setCredentialsErrorMessage: setCredentialsErrorMessageConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const CredentialsErrorBoundary = connector(CredentialsErrorBoundaryPresenter);
